import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// react-resizable-panels v4 — Group / Panel / Separator (renamed from
// PanelGroup / Panel / PanelResizeHandle in v3). Orientation is a prop on Group.
import { Group, Panel, Separator } from 'react-resizable-panels';
import { ArrowLeft, X } from 'lucide-react';
import { cn } from '../lib/cn';

type Direction = 'horizontal' | 'vertical';

type OpenOpts = {
  /** 'horizontal' = side-by-side (primary left, detail right).
   *  'vertical'   = stacked     (primary top,  detail bottom). */
  direction?: Direction;
  /** Initial size of the detail panel, in percent. The primary takes the rest. */
  defaultSize?: number;
  /** Anything renderable — a detail view, a form, a JSON inspector, etc. */
  content: ReactNode;
};

type SplitState = OpenOpts & { open: boolean };

type SplitApi = {
  open: (opts: OpenOpts) => void;
  close: () => void;
  isOpen: boolean;
};

const SplitCtx = createContext<SplitApi | null>(null);

/** Wraps the routed page content and exposes `useSplit()` so any descendant
 *  can pop open a secondary panel. The primary (the routed page) keeps
 *  rendering — `open` only mounts the second pane and a draggable divider
 *  around it. `close` unmounts the second pane; the primary returns to full
 *  area. */
export function SplitProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SplitState>({
    open: false,
    direction: 'horizontal',
    defaultSize: 40,
    content: null,
  });

  const open = useCallback((opts: OpenOpts) => {
    setState({
      open: true,
      direction: opts.direction ?? 'horizontal',
      defaultSize: opts.defaultSize ?? 40,
      content: opts.content,
    });
  }, []);

  const close = useCallback(() => {
    setState((s) => ({ ...s, open: false, content: null }));
  }, []);

  // Close any open drawer when the route changes — a panel opened on one page
  // shouldn't linger over the next one.
  const { pathname } = useLocation();
  useEffect(() => {
    close();
  }, [pathname, close]);

  const api = useMemo<SplitApi>(() => ({ open, close, isOpen: state.open }), [
    open,
    close,
    state.open,
  ]);

  return (
    <SplitCtx.Provider value={api}>
      <SplitShell state={state} onClose={close}>
        {children}
      </SplitShell>
    </SplitCtx.Provider>
  );
}

export function useSplit(): SplitApi {
  const ctx = useContext(SplitCtx);
  if (!ctx) throw new Error('useSplit must be used inside <SplitProvider>');
  return ctx;
}

/** The detail surface — a header bar (Back / Close) over scrollable content.
 *  Shared by the desktop resizable panel and the mobile overlay drawer. */
function DetailBody({
  onClose,
  isHorizontal,
  children,
}: {
  onClose: () => void;
  isHorizontal: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        'flex h-full w-full flex-col bg-card',
        isHorizontal
          ? 'border-l border-border shadow-[-8px_0_30px_rgba(21,23,28,0.06)]'
          : 'border-t border-border shadow-[0_-8px_30px_rgba(21,23,28,0.06)]',
      )}
    >
      <div className="flex h-10 shrink-0 items-center justify-between border-b border-border bg-card px-2">
        {/* Back = return to the full page (same action as close, but reads as
            "go back" rather than a bare ✕). */}
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('action.back')}
        </button>
        <button
          type="button"
          aria-label="Close panel"
          onClick={onClose}
          className="inline-flex items-center justify-center rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-auto">{children}</div>
    </div>
  );
}

/** Phone check — below the app's `md` breakpoint (the same cut as the sidebar's
 *  hamburger). A resizable side-by-side split is unusable at phone width, so the
 *  detail opens as a 90%-width overlay drawer there instead. */
function useIsMobile(): boolean {
  const query = '(max-width: 767px)';
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMobile(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return mobile;
}

function SplitShell({
  state,
  onClose,
  children,
}: {
  state: SplitState;
  onClose: () => void;
  children: ReactNode;
}) {
  const isMobile = useIsMobile();
  const isHorizontal = state.direction === 'horizontal';

  // Phone: the primary page keeps the full screen; the detail opens as a 90%
  // overlay drawer from the right over it, with a tap-to-dismiss backdrop.
  if (isMobile) {
    return (
      <div className="relative h-full w-full">
        <div className="h-full w-full overflow-auto">{children}</div>
        {state.open ? (
          <>
            <div
              className="fixed inset-0 z-[55] bg-foreground/40"
              aria-hidden
              onClick={onClose}
            />
            <div className="fixed inset-y-0 right-0 z-[60] w-[90vw]">
              <DetailBody onClose={onClose} isHorizontal={isHorizontal}>
                {state.content}
              </DetailBody>
            </div>
          </>
        ) : null}
      </div>
    );
  }

  // Desktop: the primary (routed page) is ALWAYS the first Panel of the same
  // Group — even when closed it's a single-panel group. Only the detail Panel
  // mounts/unmounts on toggle, so opening the split never remounts the page
  // (which would reset its local state, e.g. the calendar's view + filters).
  return (
    <Group orientation={isHorizontal ? 'horizontal' : 'vertical'} className="h-full w-full">
      {/* Stable ids so the detail panel's defaultSize is honoured when it mounts
          (without them react-resizable-panels falls back to a useId that changes
          each mount, and the panel opens at its minSize instead of defaultSize). */}
      <Panel id="split-primary" minSize="20%" className="overflow-auto">
        {children}
      </Panel>

      {!state.open ? null : (
        <>
          <Separator
            className={cn(
              'group relative flex items-center justify-center bg-transparent',
              isHorizontal ? 'w-3 cursor-col-resize' : 'h-3 cursor-row-resize',
            )}
          >
            {/* Hairline along the full length of the divider. */}
            <span
              className={cn(
                'absolute bg-border transition-colors group-hover:bg-primary/60',
                isHorizontal
                  ? 'bottom-0 left-1/2 top-0 w-px -translate-x-1/2'
                  : 'left-0 right-0 top-1/2 h-px -translate-y-1/2',
              )}
            />
            {/* Centered thick grip — visual affordance for dragging. */}
            <span
              className={cn(
                'relative rounded-full bg-border transition-colors group-hover:bg-primary',
                isHorizontal ? 'h-8 w-1' : 'h-1 w-8',
              )}
            />
          </Separator>

          <Panel
            id="split-detail"
            defaultSize={`${Math.max(state.defaultSize ?? 40, 30)}%`}
            minSize="30%"
            className="overflow-auto"
          >
            <DetailBody onClose={onClose} isHorizontal={isHorizontal}>
              {state.content}
            </DetailBody>
          </Panel>
        </>
      )}
    </Group>
  );
}
